import { IClientData } from '../../../../Assembler/dist/registry/iface/iClientData.js';
import { JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import type { GetParentJsonValueHandler } from '../../../../Assembler/dist/registry/iface/getParentJsonValueHandler.js';
import type { ITransientStore } from '../../../../Assembler/dist/registry/iface/iTransientStore.js';
import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { CounterManager } from '../dataReloadable/counterManager.js';
import { CounterFilter } from '../../registryCommon/dynamicData/counterFilter.js';

/**
 * Purpose:
 * Server-side CLIENT-DATA preparer for the counter capability. Produces the RAW island — the counter value
 * already resolved server-side plus the key it projects under — that a browser-side handler will later
 * project into component JSON WITHOUT a server round-trip.
 *
 * Responsibilities:
 * - Read the CounterKey / Persist params by REUSING CounterFilter
 * - Resolve the value through the SAME order as CounterData: this merge's action result, then the opt-in
 *   App_Data-backed store, then the component's own base value
 * - Ship the outcome only; the STORE never reaches the browser
 *
 * Counter state is server state, which is why the resolution happens here and the browser is handed the
 * resolved value. This lives in the SAME registry root as CounterData — the preparer belongs beside the
 * data provider whose output it mirrors. It never runs on the pure server IData path: the
 * applyDynamicClientData merge step invokes it for components bound in dynamicclientdata.json.
 */
export class CounterClientData extends IClientData {
    private _counterManager: CounterManager | null = null;

    /**
     * Wires the App_Data-backed counter store used for cross-request persistence. A client host registers
     * without one: the preparer then resolves transient-or-base only, matching how CounterData behaves with
     * no manager wired.
     */
    initialize(counterManager: CounterManager): void {
        this._counterManager = counterManager;
    }

    /**
     * Gets the full type name for DynamicClientData operation tracking.
     */
    get typeName(): string {
        return 'CounterClientData';
    }

    /**
     * Produces the raw counter island in a single server-side pass, resolving through the same order as the
     * server data provider so the two tiers cannot disagree on the value.
     */
    getClientData(appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, resolvedAppSite: string, resolvedComponentName: string, paramJson: JsonObject, componentJson: JsonObject, transientStore: ITransientStore, configJson: JsonObject | null = null, getParentJsonValue: GetParentJsonValueHandler | null = null): JsonObject {
        void appView; void appFile; void componentDepth; void resolvedAppSite; void resolvedComponentName; void configJson; void getParentJsonValue;
        const rawDataJson = new JsonObject();

        try {
            // REUSE the shared producer — no duplicated key/persist reads or value coercion.
            const counterKey = CounterFilter.readCounterKey(paramJson);
            const persist = CounterFilter.readPersistFlag(paramJson);
            const transientCounterKey = CounterFilter.buildTransientCounterKey(componentName, counterKey);
            const storedCounter = this._counterManager !== null
                ? this._counterManager.tryGetValue(CounterManager.buildCounterKey(appSite, componentName, counterKey))
                : { found: false, value: 0 };

            let counterValue: number;
            if (transientStore.containsKey(transientCounterKey) === true) {
                const transientValue = transientStore.getValue(transientCounterKey);
                if (transientValue === null) {
                    return rawDataJson;
                }
                counterValue = CounterFilter.toCounterValue(transientValue);
            } else if (persist === true && storedCounter.found === true) {
                counterValue = storedCounter.value;
            } else if (componentJson !== null && componentJson !== undefined && componentJson.containsKey(counterKey) === true) {
                counterValue = CounterFilter.toCounterValue(componentJson.get(counterKey));
            } else {
                return rawDataJson;
            }

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`CounterClientData.GetClientData: Returned raw counter for ${componentName}`, 'CounterClientData');
            }

            return CounterFilter.buildRawCounter(counterKey, counterValue);
        } catch (ex) {
            Logger.error((ex as Error).message, 'CounterClientData.GetClientData');
        }

        return rawDataJson;
    }
}
